import { AgentDisplay, UserInfos } from '@/api/common/type'
import { Base, Component, Watch } from '@/vue-property-decorator'
import { CanClickWhenForceBind } from '@/views/center/security/store'
import {
  Col,
  Dropdown,
  Icon,
  Menu,
  Row
} from '@/plugins/ant-design-vue/tsx-support'
import { CommonCopy } from '@/components/business-components'
import { Currency } from '@/config/currencies.config'
import { FinanceWithdrawTabsEnum } from '@/views/finance/withdraw/const'
import { GlobalConst } from '@/context'
import { NoticeType } from '@/views/notice/const'
import { TabsEnum as ReportTabsEnum } from '@/views/center/report/config'
import { RouteName } from '@/router/config'
import { TabsEnum } from '@/views/center/security/const'
import { Throttle } from '@/utils/Decorator'
import { apiGetUserFeedback } from '@/api/feedback'
import { apiVersionUpdateList } from '@/api/version-update'
import { createSvgSprite } from '@/utils/business-utils/assets'
import { to } from 'await-to-js'
import { useFeedbackStore } from '@/views/feedback/store'
import { useI18n } from '@/i18n'
import { useMainStore } from '@/store/index'
import { useNoticeStore } from '@/views/notice/store'
import BusinessUtils from '@/utils/business-utils'
import Modal from '@/controller/Modal'
import Skin from '@/controller/Skin'
import TooltipsComp from '@/components/business-components/tooltips'
import User from '@/controller/User'
import VipBadge from '../vip-badge'
import VueI18n from 'vue-i18n'
import style from './style.module.scss'
import usePromoteStore from '@/views/promote/store'

enum CenterMenusEnum {
  ACCOUNT_DETAILS,
  BETTING_RECORD,
  PERSONAL_STATEMENT,
  WITHDRAWAL_MANAGEMENT,
  PROMOTE,
  SETTINGS,
  CENTER_WALLET,
  SECURITY,
  MUSIC,
  FEEDBACK,
  ABOUT_US,
  VERSION_UPDATE,
  LOGOUT,
  NOTICE
}

interface MenuItemType {
  id: number
  label: VueI18n.TranslateResult
  icon: {
    functional: boolean
    render: () => VueTsxSupport.JSX.Element
  }
  solid?: boolean
  hidden?: boolean
  unRead?: string
}

@Component<PersonalCenter>({
  name: 'PersonalCenter'
})
export default class PersonalCenter extends Base {
  store = useMainStore()
  private versionUpdateVisible = true

  private get promoteAdvertise() {
    return usePromoteStore().getAgentModeData.advertise
  }

  @Watch('store.language', { immediate: true })
  async languageKeyChange(oldV: boolean, newV: boolean) {
    if (newV && oldV !== newV) {
      usePromoteStore().getPromoteAdvertise()
    }
  }

  created() {
    this.initVersionUpdate()
  }

  private get myCenterMenuData(): Array<MenuItemType> {
    const { currentTheme, brandLogoInfos, userInfos, systemInfos, showMusic } =
      useMainStore()
    const { isCyanBlue } = Skin.currentTheme()
    const { t } = useI18n()
    const currencyIconMap = Currency.commonIconMap()
    const { unreadMessageCount } = useNoticeStore()
    return [
      {
        id: CenterMenusEnum.PROMOTE,
        icon: {
          functional: true,
          render: () => {
            return (
              <icon-sprite
                attrs={{
                  color: currentTheme['--theme-secondary-color-finance']
                }}
                sprite={createSvgSprite('icon_grzx_tg')}
              />
            )
          }
        },
        label: t('lobby.header.menus.promote', {
          content: this.promoteAdvertise ? `-${this.promoteAdvertise}` : ''
        }),
        solid: true,
        hidden: systemInfos?.agentDisplayEntrance == AgentDisplay.CLOSE
      },
      {
        id: CenterMenusEnum.ACCOUNT_DETAILS,
        icon: {
          functional: true,
          render: () => {
            return (
              <icon-sprite
                attrs={{
                  color: currentTheme['--theme-secondary-color-finance']
                }}
                sprite={
                  currencyIconMap[
                    (userInfos?.currency ||
                      'CNY') as unknown as keyof typeof currencyIconMap
                  ].HEADER_ZHMX
                }
              />
            )
          }
        },
        label: t('lobby.header.menus.accountDetails')
      },
      {
        id: CenterMenusEnum.BETTING_RECORD,
        icon: {
          functional: true,
          render: () => {
            return (
              <icon-sprite
                attrs={{
                  color: currentTheme['--theme-secondary-color-success']
                }}
                sprite={
                  currencyIconMap[
                    (userInfos?.currency ||
                      'CNY') as unknown as keyof typeof currencyIconMap
                  ].HEADER_TZJL
                }
              />
            )
          }
        },
        label: t('lobby.header.menus.bettingRecord')
      },
      {
        id: CenterMenusEnum.PERSONAL_STATEMENT,
        icon: {
          functional: true,
          render: () => {
            return (
              <icon-sprite
                attrs={{
                  color: currentTheme['--theme-primary-color']
                }}
                sprite={createSvgSprite('icon_grzx_grbb')}
              />
            )
          }
        },
        label: isCyanBlue
          ? t('lobby.header.menus.personalStatementCyanBlue')
          : t('lobby.header.menus.personalStatement')
      },
      {
        id: CenterMenusEnum.NOTICE,
        icon: {
          functional: true,
          render: () => {
            return (
              <icon-sprite
                class={style.messageIcon}
                attrs={{
                  color: currentTheme['--theme-primary-color']
                }}
                sprite={
                  unreadMessageCount > 0
                    ? createSvgSprite('icon_grzx_xxzx1')
                    : createSvgSprite('icon_grzx_xxzx2')
                }
              />
            )
          }
        },
        unRead: this.getMessageCount,
        label: t('lobby.header.menus.messageCenter'),
        solid: true
      },
      {
        id: CenterMenusEnum.CENTER_WALLET,
        icon: {
          functional: true,
          render: () => {
            return (
              <icon-sprite
                class={style.messageIcon}
                attrs={{
                  color: currentTheme['--theme-primary-color']
                }}
                sprite={createSvgSprite('icon_wd_zxqb2')}
              />
            )
          }
        },

        label: t('lobby.finance.center.wallet.title')
      },
      {
        id: CenterMenusEnum.WITHDRAWAL_MANAGEMENT,
        icon: {
          functional: true,
          render: () => {
            return (
              <icon-sprite
                attrs={{
                  color: currentTheme['--theme-secondary-color-error']
                }}
                sprite={createSvgSprite('icon_grzx_txgl')}
              />
            )
          }
        },
        label: t('lobby.header.menus.withdrawalManagement')
      },

      {
        id: CenterMenusEnum.SETTINGS,
        icon: {
          functional: true,
          render: () => {
            return (
              <icon-sprite
                attrs={{
                  color: currentTheme['--theme-primary-color']
                }}
                sprite={createSvgSprite('icon_grzx_sz')}
              />
            )
          }
        },
        label: t('lobby.header.menus.settings')
      },
      {
        id: CenterMenusEnum.SECURITY,
        icon: {
          functional: true,
          render: () => {
            return (
              <icon-sprite
                class={['securityIcon']}
                attrs={{
                  color: currentTheme['--theme-secondary-color-success']
                }}
                sprite={createSvgSprite('icon_grzx_aqzx')}
              />
            )
          }
        },
        label: t('lobby.header.menus.security'),
        solid: true
      },
      {
        id: CenterMenusEnum.MUSIC,
        icon: {
          functional: true,
          render: () => {
            return (
              <icon-sprite
                attrs={{
                  color: currentTheme['--theme-primary-color']
                }}
                sprite={createSvgSprite('icon_grzx_yy')}
              />
            )
          }
        },
        label: t('lobby.mine.itemList.music'),
        hidden: !showMusic
      },
      {
        id: CenterMenusEnum.FEEDBACK,
        icon: {
          functional: true,
          render: () => {
            return (
              <icon-sprite
                attrs={{
                  color: currentTheme['--theme-primary-color']
                }}
                sprite={createSvgSprite('icon_grzx_yjfk')}
              />
            )
          }
        },
        unRead: this.getFeedBackCount,
        label: t('lobby.header.menus.feedback')
      },
      ...(brandLogoInfos?.aboutUsStatus === 1
        ? [
            {
              id: CenterMenusEnum.ABOUT_US,
              icon: {
                functional: true,
                render: () => {
                  return (
                    <icon-sprite
                      attrs={{
                        color: currentTheme['--theme-secondary-color-success']
                      }}
                      sprite={createSvgSprite('icon_grzx_gywm')}
                    />
                  )
                }
              },
              label: `${t('lobby.header.menus.about')} ${
                brandLogoInfos.brandName
              }`
            }
          ]
        : []),
      {
        id: CenterMenusEnum.VERSION_UPDATE,
        icon: {
          functional: true,
          render: () => {
            return (
              <icon-sprite
                attrs={{
                  color: currentTheme['--theme-primary-color']
                }}
                sprite={createSvgSprite('icon_grzx_bbgx')}
              />
            )
          }
        },
        label: t('lobby.mine.itemList.versionUpdate'),
        hidden: this.versionUpdateVisible
      },
      {
        id: CenterMenusEnum.LOGOUT,
        icon: {
          functional: true,
          render: () => {
            return (
              <icon-sprite
                class={[style.loginOutIcon, 'loginOutIcon']}
                attrs={{
                  color: currentTheme['--theme-secondary-color-error']
                }}
                sprite={createSvgSprite('icon_grzx_tc')}
              />
            )
          }
        },
        label: t('lobby.header.menus.logout')
      }
    ].filter((item) => !item.hidden)
  }

  private get getMessageCount() {
    const { unreadMessageCount } = useNoticeStore()
    return unreadMessageCount > 99 ? '99+' : `${unreadMessageCount}`
  }

  private get getFeedBackCount() {
    const { unRead } = useFeedbackStore()
    return unRead > 99 ? '99+' : `${unRead}`
  }

  private async initVersionUpdate() {
    const [error, data] = await to(apiVersionUpdateList())
    if (!error) {
      const result = data.data.data
      if (result?.length) {
        this.versionUpdateVisible = false
      }
    }
  }

  private onMenuClick(payload: { key: CenterMenusEnum }) {
    const key = payload.key
    switch (key) {
      // logout
      case CenterMenusEnum.LOGOUT:
        User.logoutModal()
        break
      // 账户明细
      case CenterMenusEnum.ACCOUNT_DETAILS:
        this.$router.push({
          name: RouteName.REPORT,
          query: {
            current: ReportTabsEnum.ACCOUNT_DETAILS
          }
        })
        break
      // 投注记录
      case CenterMenusEnum.BETTING_RECORD:
        this.$router.push({
          name: RouteName.REPORT,
          query: {
            current: ReportTabsEnum.BETTING_RECORD
          }
        })
        break
      // 个人报表
      case CenterMenusEnum.PERSONAL_STATEMENT:
        this.$router.push({
          name: RouteName.REPORT,
          query: {
            current: ReportTabsEnum.PERSONAL_STATEMENT
          }
        })
        break
      case CenterMenusEnum.CENTER_WALLET:
        this.$router.push({
          name: RouteName.CENTER_WALLET
        })
        break
      // 提现管理
      case CenterMenusEnum.WITHDRAWAL_MANAGEMENT:
        const {
          validateConfig: { hasWithdrawPwd }
        } = useMainStore()
        if (!hasWithdrawPwd) {
          const { t } = useI18n()
          Modal.message({
            type: 'warning',
            content: t('lobby.center.security.forceBindWidthdrawPass')
          })
          this.$router.push({
            name: RouteName.SECURITY,
            query: {
              current: TabsEnum.WITHDRAWPASS.toString(),
              isCallbackWithdraw: FinanceWithdrawTabsEnum.ACCOUNT.toString()
            }
          })
        } else {
          this.$router.push({
            name: RouteName.WITHDRAW,
            query: {
              current: `${FinanceWithdrawTabsEnum.ACCOUNT}`
            }
          })
        }
        break
      // 推广
      case CenterMenusEnum.PROMOTE:
        this.$router.push({
          name: RouteName.PROMOTE
        })
        break
      // 设置
      case CenterMenusEnum.SETTINGS:
        this.$router.push({
          name: RouteName.SETTING
        })
        break
      // 安全中心
      case CenterMenusEnum.SECURITY:
        this.$router.push({
          name: RouteName.SECURITY
        })
        break
      // 音乐
      case CenterMenusEnum.MUSIC:
        Modal.open('musicModal')
        break
      //有奖反馈
      case CenterMenusEnum.FEEDBACK:
        this.$router.push({
          name: RouteName.NOTICE,
          query: {
            noticeType: `${NoticeType.FEEDBACK}`
          }
        })
        break
      //关于我们
      case CenterMenusEnum.ABOUT_US:
        this.$router.push({
          name: RouteName.ABOUT_US
        })
        break
      // 版本更新
      case CenterMenusEnum.VERSION_UPDATE:
        this.$router.push({
          name: RouteName.VERSION_UPDATE
        })
        break
      case CenterMenusEnum.NOTICE:
        const { unreadAnnouncement, unreadNotice } = useNoticeStore()
        let type: NoticeType
        if (unreadNotice > 0) {
          type = NoticeType.NOTICE
        } else if (unreadAnnouncement > 0) {
          type = NoticeType.ANNOUNCEMENT
        } else {
          type = NoticeType.NOTICE
        }
        this.$router.push({
          name: RouteName.NOTICE,
          query: {
            noticeType: type + ''
          }
        })
        break
      default:
        break
    }
  }

  /**
   * 在侧边栏初始化时请求未读反馈数量并存放到store供头部消息使用
   * 默认时5 分钟的缓存 5 * 60 * 1000 = 300000
   */
  @Throttle(300000)
  private async queryFeedBackList() {
    const { setUnRead } = useFeedbackStore()
    const payload = {
      page: 0,
      pageSize: 3
    }

    const data = await apiGetUserFeedback(payload)
    const unRead = data?.unRead ?? 0
    setUnRead(unRead)
  }

  render() {
    const { userInfos } = useMainStore()
    const { isStake, isGalaxyGold, isCrownGold } = Skin.currentTheme()
    const { portrait_id, platfromid, username } = userInfos as UserInfos
    return (
      <Dropdown.Tsx
        // 显示的时候，需要区请求有奖反馈的小红点
        onVisibleChange={(visible: boolean) => {
          if (!visible) {
            return
          }
          this.queryFeedBackList()
        }}
        class={[CanClickWhenForceBind, 'personal-center']}
        trigger={['hover']}
        placement="bottomCenter"
        overlayClassName={'personal-dropdown'}
      >
        <template slot="overlay">
          <Menu.Tsx onClick={this.onMenuClick}>
            {this.myCenterMenuData.map(
              ({ icon, label, id, solid = false, unRead }) => (
                <Menu.Item.Tsx
                  title={label as string}
                  key={id}
                  class={{
                    [CanClickWhenForceBind]: id === CenterMenusEnum.LOGOUT
                  }}
                >
                  <Row.Tsx type={'flex'} align={'middle'} class={style.item}>
                    <Col.Tsx class={style['icon-box']}>
                      <Icon.Tsx component={icon} />

                      {!!unRead && Number(unRead) > 0 && (
                        <TooltipsComp
                          charge={1}
                          top={BusinessUtils.px2rem(-20)}
                          left={BusinessUtils.px2rem(16)}
                          text={<span>{unRead}</span>}
                        />
                      )}
                    </Col.Tsx>
                    <Col.Tsx class={GlobalConst.CssClassName.Lobby.Ellipsis}>
                      {label}
                    </Col.Tsx>
                  </Row.Tsx>
                  {solid && (
                    <div class={[style.solidWrap, 'solidWrap']}>
                      <div class={style.solid} />
                    </div>
                  )}
                </Menu.Item.Tsx>
              )
            )}
          </Menu.Tsx>
        </template>
        <div class={style['menu-groups']}>
          {!isGalaxyGold && !isCrownGold ? (
            <div class={[style.commonBox, 'commonBox']}>
              <div class={[style.avatar, 'avatar']}>
                <my-img
                  src={portrait_id}
                  width={isStake ? 32 : 56}
                  height={isStake ? 32 : 56}
                />
                <div class={style.vipWrap}>
                  <VipBadge />
                </div>
              </div>
              <div class={style.wrap}>
                <div class={[style.wrapItem, 'wrapItem']}>
                  <span class={[style.platfromId, 'platfrom-id-txt']}>
                    {platfromid}
                  </span>
                </div>
                <div class={[style.wrapItem, 'wrapItem']}>
                  <span class={[style.username, 'user-name']}>
                    ID: {username}
                  </span>
                  <CommonCopy
                    class={[style.copyIcon, 'copy-icon']}
                    text={String(username)}
                  />
                </div>
              </div>
            </div>
          ) : (
            <span class={style.galaxyGoldpPlatfromId}>{platfromid}</span>
          )}

          <icon-sprite
            sprite={createSvgSprite('comm_icon_sort')}
            class={[style.dropdown, 'dropdown-arrow']}
          />
        </div>
      </Dropdown.Tsx>
    )
  }
}
